import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, SectionCard, StatusChip } from './ui';
import { integrationApiService } from '../services/integrationApi';
import { useToast } from '../contexts/ToastContext';

const REASON_LABEL: Record<string, string> = {
  barcode_on_other_sku: 'Barcode on another SKU',
  identity_review: 'Needs identity check',
  archived_listing: 'Archived listing',
  title: 'Similar title',
  handle: 'Same handle',
};

interface ShopifyListingHoldsProps {
  integrationId: string;
}

/**
 * Products the sync refused to create because a Shopify listing might already
 * be the same item. Jordan confirms the listing, or allows a new one.
 */
const ShopifyListingHolds: React.FC<ShopifyListingHoldsProps> = ({ integrationId }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const queryKey = ['shopify-listing-holds', integrationId];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => integrationApiService.listShopifyListingHolds(integrationId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const confirm = useMutation({
    mutationFn: (input: { productId: string; shopifyProductId: string }) =>
      integrationApiService.confirmShopifyListingHold(integrationId, input.productId, input.shopifyProductId),
    onSuccess: () => {
      showToast('The next sync will update that listing.', 'success');
      refresh();
    },
    onError: (error: unknown) => {
      showToast(error instanceof Error ? error.message : 'Could not confirm the listing', 'error');
    },
  });

  const allowCreate = useMutation({
    mutationFn: (productId: string) => integrationApiService.allowShopifyListingCreate(integrationId, productId),
    onSuccess: () => {
      showToast('The next sync may create a new listing for this product.', 'success');
      refresh();
    },
    onError: (error: unknown) => {
      showToast(error instanceof Error ? error.message : 'Could not allow a new listing', 'error');
    },
  });

  const holds = data || [];
  const busy = confirm.isPending || allowCreate.isPending;

  return (
    <SectionCard
      title="Listing matches to confirm"
      subtitle="Sync will not create a second Shopify product while one of these might already be the same item."
      padded
    >
      {isLoading && (
        <Typography variant="body2" color="text.secondary">Loading matches…</Typography>
      )}
      {isError && (
        <Typography variant="body2" color="error">Could not load listing matches.</Typography>
      )}
      {!isLoading && !isError && holds.length === 0 && (
        <EmptyState
          size="compact"
          title="No listings waiting"
          description="When a sync is not sure which Shopify product to update, it shows up here instead of creating another one."
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {holds.map((hold) => (
          <Box key={hold.productId} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {hold.productName || hold.productSku || hold.productId}
              </Typography>
              {hold.productSku && (
                <Typography variant="caption" color="text.secondary">SKU {hold.productSku}</Typography>
              )}
              <StatusChip label={REASON_LABEL[hold.reason] || 'Needs confirmation'} tone="caution" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">{hold.message}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {hold.candidates.filter((candidate) => candidate.productId).map((candidate) => (
                <Button
                  key={candidate.productId}
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => confirm.mutate({ productId: hold.productId, shopifyProductId: candidate.productId })}
                >
                  Use {candidate.title}
                </Button>
              ))}
              <Button
                size="small"
                variant="text"
                disabled={busy}
                onClick={() => allowCreate.mutate(hold.productId)}
              >
                Create a new listing
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
};

export default ShopifyListingHolds;
